import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  TagIcon, ArrowUpTrayIcon, MagnifyingGlassIcon, ArrowPathIcon, XMarkIcon, SparklesIcon,
  CheckCircleIcon, ExclamationTriangleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import icd10Service from '../../services/icd10Service';
import { errText } from '../../services/scheduleService';
import TariffPanel from './TariffPanel';
import NappiPanel from './NappiPanel';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-ZA'));
const when = (d) => (d ? new Date(d).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const SOURCE_LABEL = { code: 'Code', keyword: 'Keyword', fuzzy: 'Fuzzy', concept: 'Concept' };
const SOURCE_TONE = {
  code: 'bg-brand-navy text-white',
  keyword: 'bg-blue-100 text-blue-700',
  fuzzy: 'bg-slate-200 text-slate-600',
  concept: 'bg-teal-100 text-teal-700',
};

const Flag = ({ on, children, tone = 'bg-slate-100 text-slate-600', title }) =>
  on ? <span title={title} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}>{children}</span> : null;

/**
 * Codes & Tariffs (admin). For now: the SA ICD-10 Master Industry Table — the
 * active release, importing a new MIT (preview first, then apply), the state of
 * concept search, and a search box to try the same hybrid search the assistant
 * uses. Below it, TariffPanel: tariff codes (a PLACEHOLDER release for now),
 * fee schedules and the fee calculator.
 */
const CodesTariffs = () => {
  const [status, setStatus] = useState(null);
  const [releases, setReleases] = useState([]);
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([icd10Service.status(), icd10Service.releases()]);
      setStatus(s);
      setReleases(r);
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while embeddings are building.
  const running = !!status?.vectors?.job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const buildEmbeddings = async () => {
    setStarting(true);
    try {
      await icd10Service.buildEmbeddings();
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setStarting(false);
    }
  };

  const active = status?.active;
  const v = status?.vectors;

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-deep">Codes &amp; Tariffs</h1>
        <p className="mt-1 text-slate-500">The coding reference data that drives billing. Shared by every practice.</p>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        {/* Active release */}
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-gradient text-white">
              <TagIcon className="h-6 w-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-slate-800">ICD-10 Master Industry Table</h2>
              {status === null ? (
                <p className="text-sm text-slate-500">Loading…</p>
              ) : active ? (
                <>
                  <p className="truncate text-sm text-slate-500" title={active.source_filename}>{active.source_filename}</p>
                  <p className="text-xs text-slate-400">Imported {when(active.imported_at)}{active.imported_by_name ? ` by ${active.imported_by_name}` : ''}</p>
                </>
              ) : (
                <p className="text-sm text-slate-500">No ICD-10 file imported yet.</p>
              )}
            </div>
          </div>
          {active && (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <dt className="text-xs text-slate-500">Codes in file</dt>
                <dd className="text-lg font-bold text-slate-800">{fmt(status.counts.current_codes)}</dd>
              </div>
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <dt className="text-xs text-slate-500">Billable today</dt>
                <dd className="text-lg font-bold text-slate-800">{fmt(status.counts.billable_codes)}</dd>
              </div>
            </dl>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setImportOpen(true)}
              disabled={!status?.canImport}
              title={status && !status.canImport ? 'Imports are only allowed from the OmniPrac administration practice' : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="h-5 w-5" /> Import new MIT file
            </button>
          </div>
        </section>

        {/* Concept search */}
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-teal-50 text-brand-teal">
              <SparklesIcon className="h-6 w-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-slate-800">Concept search (vectors)</h2>
              <p className="text-sm text-slate-500">
                Finds codes by meaning — “can’t catch his breath” finds dyspnoea. Keyword search works without it.
              </p>
            </div>
          </div>
          <div className="mt-4 text-sm">
            {!v ? null : !v.extensionAvailable ? (
              <SetupNote>
                pgvector is not installed on the database server. On the Postgres host run
                <code className="mx-1 rounded bg-white px-1">sudo apt install postgresql-18-pgvector</code>
                then apply <code className="rounded bg-white px-1">backend/db/08_icd10_vectors.sql</code>.
              </SetupNote>
            ) : !v.tableReady ? (
              <SetupNote>
                pgvector is available — apply <code className="rounded bg-white px-1">backend/db/08_icd10_vectors.sql</code> to omniMain to switch concept search on.
              </SetupNote>
            ) : (
              <>
                <div className="mb-1 flex justify-between text-xs text-slate-500">
                  <span>{v.job?.running ? `Building… ${fmt(v.job.done)} of ${fmt(v.job.total)} this run` : v.pending ? `${fmt(v.pending)} codes still to embed` : 'Every code is embedded'}</span>
                  <span>{fmt(v.embedded)} / {fmt(v.total)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full bg-brand-teal transition-all" style={{ width: `${v.total ? (v.embedded / v.total) * 100 : 0}%` }} />
                </div>
                {v.job?.error && <p className="mt-2 text-xs text-red-600">Last run failed: {v.job.error}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-400">{v.model} · runs on this server</span>
                  {(v.pending > 0 || v.job?.error) && (
                    <button
                      onClick={buildEmbeddings}
                      disabled={starting || v.job?.running}
                      className="rounded-lg border border-brand-teal px-3 py-1.5 text-xs font-semibold text-brand-teal hover:bg-teal-50 disabled:opacity-50"
                    >
                      {v.job?.running ? 'Building…' : 'Build embeddings'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {active && <SearchPanel conceptReady={!!v?.tableReady && v.embedded > 0} />}

      {/* Release history */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
          <ClockIcon className="h-5 w-5 text-brand-teal" />
          <h2 className="flex-1 font-bold text-slate-800">Import history</h2>
          <button onClick={load} title="Refresh" className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy">
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2.5 font-semibold">File</th>
                <th className="px-3 py-2.5 font-semibold">Imported</th>
                <th className="px-3 py-2.5 text-right font-semibold">Codes</th>
                <th className="px-3 py-2.5 text-right font-semibold">Added</th>
                <th className="px-3 py-2.5 text-right font-semibold">Changed</th>
                <th className="px-5 py-2.5 text-right font-semibold">Removed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {releases.map((r) => (
                <tr key={r.release_id}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">{r.source_filename}</span>
                      {r.status === 'active' && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">Active</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-500">{when(r.imported_at)}<div className="text-xs text-slate-400">{r.imported_by_name}</div></td>
                  <td className="px-3 py-3 text-right">{fmt(r.row_count)}</td>
                  <td className="px-3 py-3 text-right">{fmt(r.added_count + r.restored_count)}</td>
                  <td className="px-3 py-3 text-right">{fmt(r.modified_count)}</td>
                  <td className="px-5 py-3 text-right">{fmt(r.removed_count)}</td>
                </tr>
              ))}
              {releases.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-400">No imports yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <TariffPanel />
      <NappiPanel />

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onImported={load} />}
    </div>
  );
};

const SetupNote = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800">
    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
    <p className="text-xs leading-relaxed">{children}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Search — the same hybrid search the assistant will use, so its results can
// be checked by eye.
// ---------------------------------------------------------------------------
const SearchPanel = ({ conceptReady }) => {
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const run = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    setErr('');
    try {
      setOut(await icd10Service.search(q.trim(), { limit: 20, all }));
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <form onSubmit={run} className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
        <div className="relative min-w-[16rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={conceptReady ? 'Try a code, words, or how a doctor would say it…' : 'Try a code (J45.9) or words (fractured femur)…'}
            className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="rounded border-slate-300" />
          Include non-billable headings
        </label>
        <button disabled={busy || q.trim().length < 2} className="rounded-lg bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
          {busy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {err && <p className="px-5 py-3 text-sm text-red-600">{err}</p>}

      {out && (
        <>
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 px-5 py-2 text-xs text-slate-500">
            <span>{out.results.length} result{out.results.length === 1 ? '' : 's'} · {out.ms} ms · searched by</span>
            {out.retrievers.map((s) => <span key={s} className={`rounded px-1.5 py-0.5 font-semibold ${SOURCE_TONE[s]}`}>{SOURCE_LABEL[s]}</span>)}
            {out.concept === 'not_set_up' && <span className="text-slate-400">(concept search not set up)</span>}
            {out.concept === 'unavailable' && <span className="text-amber-600">(concept search unavailable — see server log)</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {out.results.map((r) => (
                  <tr key={r.code} className="align-top">
                    <td className="w-24 px-5 py-2.5 font-mono font-semibold text-brand-navy">{r.code}</td>
                    <td className="px-3 py-2.5">
                      <div className="text-slate-800">{r.description}</div>
                      <div className="text-xs text-slate-400">{r.code_3} {r.code_3_desc} · {r.group_desc}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Flag on={!r.valid_clinical_use} tone="bg-red-100 text-red-700" title="Category heading — use a more specific code">Not billable</Flag>
                        <Flag on={r.valid_primary} tone="bg-green-100 text-green-700" title="May be the primary diagnosis">Primary</Flag>
                        <Flag on={r.valid_clinical_use && !r.valid_primary} title="Secondary diagnosis only">Secondary only</Flag>
                        <Flag on={r.valid_dagger} title="Aetiology code — pair with an asterisk code">Dagger †</Flag>
                        <Flag on={r.valid_asterisk} title="Manifestation code — must follow its dagger code">Asterisk *</Flag>
                        <Flag on={r.valid_sequelae}>Sequelae</Flag>
                        <Flag on={!!r.gender} tone="bg-purple-100 text-purple-700">{r.gender === 'F' ? 'Female only' : 'Male only'}</Flag>
                        <Flag on={!!r.age_range} tone="bg-purple-100 text-purple-700">Age {r.age_range}</Flag>
                      </div>
                    </td>
                    <td className="w-48 px-5 py-2.5 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {r.matched_by.map((s) => (
                          <span key={s} title={`Rank ${r.ranks[s]}`} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${SOURCE_TONE[s]}`}>
                            {SOURCE_LABEL[s]} #{r.ranks[s]}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {out.results.length === 0 && (
                  <tr><td className="px-5 py-6 text-center text-slate-400">No matching codes.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Import — choose the file, see exactly what it would change, then apply.
// ---------------------------------------------------------------------------
const ImportModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const inputRef = useRef(null);

  const choose = async (f) => {
    setFile(f);
    setPreview(null);
    setResult(null);
    setErr(null);
    setConfirmRemoval(false);
    if (!f) return;
    setBusy('preview');
    try {
      setPreview(await icd10Service.importFile(f, { dryRun: true }));
    } catch (e) {
      setErr({ message: errText(e), errors: e.response?.data?.errors });
    } finally {
      setBusy('');
    }
  };

  const apply = async () => {
    setBusy('apply');
    setErr(null);
    try {
      setResult(await icd10Service.importFile(file, { dryRun: false, allowMassRemoval: confirmRemoval }));
      onImported();
    } catch (e) {
      setErr({ message: errText(e), errors: e.response?.data?.errors });
    } finally {
      setBusy('');
    }
  };

  const nothingChanges = preview && !preview.added && !preview.modified && !preview.removed && !preview.restored;
  const s = result || preview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-bold text-brand-deep">Import ICD-10 Master Industry Table</h2>
          <button onClick={onClose} disabled={!!busy} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto px-5 py-4 text-sm">
          <p className="mb-3 text-slate-500">
            Upload the MIT workbook (.xlsx) published by the National Department of Health. You’ll see what it changes before anything is saved.
            It replaces the ICD-10 codes for <strong>every practice</strong>; codes dropped from the file are kept for old claims but no longer offered.
          </p>

          <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => choose(e.target.files?.[0] || null)} />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={!!busy || !!result}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-5 text-slate-500 hover:border-brand-teal hover:text-brand-teal disabled:opacity-60"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
            {file ? `${file.name} (${(file.size / 1048576).toFixed(1)} MB) — choose another` : 'Choose .xlsx file'}
          </button>

          {busy === 'preview' && <p className="mt-4 text-slate-500">Reading the file and comparing it with the current codes…</p>}
          {busy === 'apply' && <p className="mt-4 text-slate-500">Importing…</p>}

          {err && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <p className="font-semibold">{err.message}</p>
              {err.errors?.length > 0 && (
                <ul className="mt-2 max-h-40 list-disc overflow-y-auto pl-5 text-xs">
                  {err.errors.map((x) => <li key={x}>{x}</li>)}
                </ul>
              )}
            </div>
          )}

          {s && (
            <div className="mt-4">
              {result ? (
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-green-700">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Imported {fmt(result.rowCount)} codes as the active release.{result.embeddingsStarted ? ' Concept-search vectors are building in the background.' : ''}</span>
                </div>
              ) : (
                <p className="mb-2 text-slate-600">
                  Sheet <strong>{s.sheetName}</strong> · {fmt(s.rowCount)} codes
                  {s.sameFileAsActive && <span className="text-slate-400"> · identical to the active file</span>}
                </p>
              )}
              <div className="grid grid-cols-5 gap-2 text-center">
                {[['Added', s.added, 'text-green-700'], ['Changed', s.modified, 'text-blue-700'], ['Removed', s.removed, 'text-red-700'], ['Restored', s.restored, 'text-teal-700'], ['Unchanged', s.unchanged, 'text-slate-500']].map(([label, n, tone]) => (
                  <div key={label} className="rounded-lg bg-slate-50 px-2 py-2">
                    <div className={`text-lg font-bold ${tone}`}>{fmt(n)}</div>
                    <div className="text-[11px] text-slate-500">{label}</div>
                  </div>
                ))}
              </div>

              {s.warnings?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {s.warnings.map((w) => (
                    <li key={w} className="flex gap-2 text-xs text-amber-700"><ExclamationTriangleIcon className="h-4 w-4 shrink-0" />{w}</li>
                  ))}
                </ul>
              )}

              {!result && ['modified', 'removed', 'restored', 'added'].map((t) => s.samples?.[t]?.length > 0 && (
                <details key={t} className="mt-3 rounded-lg border border-slate-200" open={t !== 'added'}>
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold capitalize text-slate-600">
                    {t} {s[t] > s.samples[t].length ? `(first ${s.samples[t].length} of ${fmt(s[t])})` : ''}
                  </summary>
                  <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto">
                    {s.samples[t].map((c) => (
                      <li key={c.code} className="flex gap-3 px-3 py-1.5 text-xs">
                        <span className="w-16 shrink-0 font-mono font-semibold text-brand-navy">{c.code}</span>
                        <span className="flex-1 text-slate-600">{c.description}</span>
                        {c.fields?.length > 0 && <span className="text-slate-400">{c.fields.join(', ')}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              ))}

              {!result && preview?.massRemoval && (
                <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-red-700">
                  <input type="checkbox" checked={confirmRemoval} onChange={(e) => setConfirmRemoval(e.target.checked)} />
                  I have checked this is the complete MIT and want to remove these codes.
                </label>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button onClick={onClose} disabled={!!busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={apply}
              disabled={!preview || !!busy || nothingChanges || (preview.massRemoval && !confirmRemoval)}
              title={nothingChanges ? 'This file changes nothing' : undefined}
              className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
            >
              {nothingChanges ? 'No changes to import' : 'Apply import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodesTariffs;
